import React, { useState } from "react";
import { Loader2, CheckCircle2, ArrowRight, Mail, Phone, MessageSquare } from "lucide-react";
import { useT } from "../context/LanguageContext";
import { submitContactLead } from "../services/marketingApi";

const EMAIL_TO = "hello@verasist.ai";

const initial = {
  name: "",
  email: "",
  phone: "",
  company: "",
  message: "",
};

const ContactSection = () => {
  const t = useT();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await submitContactLead(form);
      setSuccess(true);
      setForm(initial);
    } catch (err) {
      // Backend submission failed — fall back to the original mailto: flow
      // so the lead isn't lost, then still show the success state.
      try {
        const body = [
          `Ad Soyad: ${form.name}`,
          `E-posta: ${form.email}`,
          `Telefon: ${form.phone || "—"}`,
          `Şirket: ${form.company || "—"}`,
          `Mesaj: ${form.message}`,
        ].join("%0D%0A");
        const mailto = `mailto:${EMAIL_TO}?subject=${encodeURIComponent("İletişim Formu")}&body=${body}`;
        window.location.href = mailto;
        setSuccess(true);
        setForm(initial);
      } catch (fallbackErr) {
        setError(err?.message || t("form.error.contact"));
      }
    } finally {
      setLoading(false);
    }
  };


  return (
    <section id="contact" className="py-20 px-6 flex items-center" style={{ minHeight: "100vh", background: "var(--section-surface-4)" }}>
      <div className="max-w-6xl mx-auto w-full">

        {/* Full-width video banner */}
        <div className="rounded-2xl overflow-hidden mb-12" style={{ border: "1px solid var(--border)" }}>
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full object-cover"
            style={{ maxHeight: "280px", display: "block" }}
            poster="/images/pages/hero-contact.jpg"
          >
            <source src="/videos/contact-hero.mp4" type="video/mp4" />
          </video>
        </div>

        {/* Two-column content */}
        <div className="grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <span
            className="inline-block px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider mb-4"
            style={{
              background: "var(--card-bg)",
              border: "1px solid var(--border)",
              color: "var(--muted)",
            }}
          >
            {t("contact.eyebrow")}
          </span>
          <h2
            className="font-bold tracking-tight"
            style={{ color: "var(--fg)", fontSize: "clamp(2rem, 4vw, 3rem)" }}
          >
            {t("contact.title1")} {" "}
            <span className="brand-gradient-text">{t("contact.title2")}</span>{t("contact.title3")}
          </h2>
          <p className="mt-4 text-[15px]" style={{ color: "var(--fg)", opacity: 0.92 }}>
            {t("contact.subtitle")}
          </p>

          <div className="mt-8 space-y-4">
            <div
              className="flex items-center gap-3 p-4 rounded-2xl neon-box"
              style={{ background: "var(--card-bg)", border: "1px solid var(--border)" }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: "var(--brand-cyan)15", color: "var(--brand-cyan)" }}
              >
                <Mail size={18} />
              </div>
              <div>
                <p className="text-[12px] uppercase tracking-wider" style={{ color: "var(--muted)" }}>
                  {t("contact.email")}
                </p>
                <p className="text-[14.5px] font-semibold" style={{ color: "var(--fg)" }}>
                  hello@verasist.ai
                </p>
              </div>
            </div>
            <div
              className="flex items-center gap-3 p-4 rounded-2xl neon-box"
              style={{ background: "var(--card-bg)", border: "1px solid var(--border)" }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: "var(--brand-coral)15", color: "var(--brand-coral)" }}
              >
                <Phone size={18} />
              </div>
              <div>
                <p className="text-[12px] uppercase tracking-wider" style={{ color: "var(--muted)" }}>
                  {t("contact.phoneLabel")}
                </p>
                <p className="text-[14.5px] font-semibold" style={{ color: "var(--fg)" }}>
                  +90 266 239 14 10
                </p>
              </div>
            </div>
            <div
              className="flex items-center gap-3 p-4 rounded-2xl neon-box"
              style={{ background: "var(--card-bg)", border: "1px solid var(--border)" }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center"
                style={{ background: "var(--brand-navy)12", color: "var(--brand-navy)" }}
              >
                <MessageSquare size={18} />
              </div>
              <div>
                <p className="text-[12px] uppercase tracking-wider" style={{ color: "var(--muted)" }}>
                  {t("contact.chatLabel")}
                </p>
                <p className="text-[14.5px] font-semibold" style={{ color: "var(--fg)" }}>
                  {t("contact.chatTime")}
                </p>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="p-7 rounded-3xl neon-box"
          style={{ background: "var(--card-bg)", border: "1px solid var(--border)" }}
        >
          {success ? (
            <div className="flex flex-col items-center text-center py-10">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center mb-4"
                style={{ background: "var(--brand-cyan)15" }}
              >
                <CheckCircle2 size={28} style={{ color: "var(--brand-cyan)" }} />
              </div>
              <h4
                className="text-[18px] font-semibold mb-2"
                style={{ color: "var(--fg)" }}
              >
                {t("form.success.title")}
              </h4>
              <p
                className="text-[14px] max-w-sm"
                style={{ color: "var(--fg)", opacity: 0.9 }}
              >
                {t("form.success.message")}
              </p>
              <button
                onClick={() => setSuccess(false)}
                type="button"
                className="mt-6 inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-[14px] font-semibold neon-btn"
              >
                {t("form.success.new")}
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="form-label">{t("form.name")} *</label>
                  <input
                    className="form-input"
                    name="name"
                    required
                    value={form.name}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="form-label">{t("form.email")} *</label>
                  <input
                    className="form-input"
                    type="email"
                    name="email"
                    required
                    value={form.email}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="form-label">{t("form.phone")}</label>
                  <input
                    className="form-input"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="form-label">{t("form.company")}</label>
                  <input
                    className="form-input"
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="form-label">{t("form.message")} *</label>
                  <textarea
                    className="form-input"
                    name="message"
                    rows={4}
                    required
                    value={form.message}
                    onChange={handleChange}
                  />
                </div>
              </div>

              {error && (
                <p
                  className="mt-4 text-[13px] px-3 py-2 rounded-lg"
                  style={{
                    background: "rgba(242, 92, 102, 0.10)",
                    color: "var(--brand-coral)",
                  }}
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-[14px] font-semibold neon-btn disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> {t("form.sending")}
                  </>
                ) : (
                  <>
                    {t("form.send")} <ArrowRight size={16} />
                  </>
                )}
              </button>
            </>
          )}
        </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
